/**
 * Biomedical Waste Handover Public Landing Page (/bmw-handover/:token).
 * R39 M7.b (plan docs/plans/sector-operations.md §2 SO-P258).
 * Modeled on ClaimLanding. Pure design tokens and NLS keys only.
 */

import React, { useState, useEffect, useCallback } from 'react'
import type { ConfirmationResult } from 'firebase/auth'
import { Button } from '../Common/Button'
import { TextInput } from '../Common/TextInput'
import { useNls } from '../../nls/nlsProvider'
import { getApiErrorMessage } from '../../services/apiConnectivity'
import { sendOutsidePhoneOtp } from '../../services/outsidePhoneOtp'
import { getApprovalsAuth } from '../../services/approvalsApp'
import {
  getBmwHandoverInfo,
  prepareBmwHandoverOtp,
  confirmBmwHandover,
} from '../../services/hosBmwService'
import type { GetBmwHandoverInfoResult, ConfirmBmwHandoverResult } from '../../types/ops'
import './bmwHandoverLanding.css'

export interface BmwHandoverLandingProps {
  token: string
}

type Step = 'LOADING' | 'INVALID' | 'PHONE_ENTRY' | 'OTP_ENTRY' | 'CONFIRMED'

export const BmwHandoverLanding: React.FC<BmwHandoverLandingProps> = ({ token }) => {
  const { t } = useNls()

  const [step, setStep] = useState<Step>('LOADING')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [info, setInfo] = useState<GetBmwHandoverInfoResult | null>(null)
  const [phone, setPhone] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [confirmedBarcodes, setConfirmedBarcodes] = useState<string[]>([])
  const [result, setResult] = useState<ConfirmBmwHandoverResult | null>(null)
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null)

  const fetchInfo = useCallback(async () => {
    if (!token) {
      setStep('INVALID')
      return
    }
    setBusy(true)
    setErrorMsg(null)
    try {
      const res = await getBmwHandoverInfo({ token })
      setInfo(res)
      setConfirmedBarcodes(res.bags.map((b) => b.barcode))
      setStep('PHONE_ENTRY')
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
      setStep('INVALID')
    } finally {
      setBusy(false)
    }
  }, [token])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch on mount
    fetchInfo()
  }, [fetchInfo])

  const handleSendOtp = async () => {
    if (!phone.trim()) return
    setBusy(true)
    setErrorMsg(null)
    try {
      const prep = await prepareBmwHandoverOtp({
        token,
        phone: phone.trim(),
      })
      if (!prep.success || prep.attemptsRemaining <= 0) {
        setErrorMsg(t('NLSOpsStaysClaimCapExhausted'))
        setBusy(false)
        return
      }

      const formattedPhone = phone.trim().startsWith('+') ? phone.trim() : `+${phone.trim()}`
      const confirmation = await sendOutsidePhoneOtp(getApprovalsAuth(), formattedPhone)
      setConfirmationResult(confirmation)
      setStep('OTP_ENTRY')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      if (
        msg.includes('resource-exhausted') ||
        msg.includes('too-many-requests') ||
        msg.includes('attempts')
      ) {
        setErrorMsg(t('NLSOpsStaysClaimCapExhausted'))
      } else {
        setErrorMsg(getApiErrorMessage(err))
      }
    } finally {
      setBusy(false)
    }
  }

  const handleToggleBarcode = (barcode: string) => {
    if (confirmedBarcodes.includes(barcode)) {
      setConfirmedBarcodes(confirmedBarcodes.filter((b) => b !== barcode))
    } else {
      setConfirmedBarcodes([...confirmedBarcodes, barcode])
    }
  }

  const handleConfirmHandover = async () => {
    if (!confirmationResult || !otpCode.trim() || confirmedBarcodes.length === 0) return
    setBusy(true)
    setErrorMsg(null)
    try {
      await confirmationResult.confirm(otpCode.trim())
      const res = await confirmBmwHandover({
        token,
        confirmedBarcodes,
      })
      setResult(res)
      setStep('CONFIRMED')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      if (msg.includes('invalid-verification-code') || msg.includes('code')) {
        setErrorMsg(t('NLSOpsApprovalInvalidCode'))
      } else {
        setErrorMsg(getApiErrorMessage(err))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bmwHandover">
      <div className="bmwHandover__card">
        <div className="bmwHandover__header">
          <h2 className="bmwHandover__title">{t('NLSOpsBmwHandoverTitle')}</h2>
          <p className="bmwHandover__subtitle">{t('NLSOpsBmwHandoverDesc')}</p>
        </div>

        {errorMsg && (
          <div className="bmwHandover__badge bmwHandover__badge--danger">
            {errorMsg}
          </div>
        )}

        {step === 'LOADING' && (
          <div className="bmwHandover__center">
            <p className="bmwHandover__subtitle">{t('NLSOpsBmwHandoverLoading')}</p>
          </div>
        )}

        {step === 'INVALID' && (
          <div className="bmwHandover__center">
            <span className="bmwHandover__badge bmwHandover__badge--danger">
              {t('NLSOpsBmwHandoverInvalid')}
            </span>
          </div>
        )}

        {step === 'PHONE_ENTRY' && info && (
          <div className="bmwHandover__form">
            <div className="bmwHandover__meta">
              <div className="bmwHandover__metaRow">
                <span className="bmwHandover__metaLabel">{info.workspaceName}</span>
                <span className="bmwHandover__metaValue">{info.placeName}</span>
              </div>
              <div className="bmwHandover__metaRow">
                <span className="bmwHandover__metaLabel">{t('NLSOpsBmwBagCount')}:</span>
                <span className="bmwHandover__metaValue">{info.bags.length}</span>
              </div>
            </div>

            <p className="bmwHandover__subtitle">
              {t('NLSOpsBmwHandoverPhonePrompt')}
            </p>

            {info.maskedCollectorPhones && info.maskedCollectorPhones.length > 0 && (
              <span className="bmwHandover__badge bmwHandover__badge--warning">
                {t('NLSOpsBmwHandoverMaskedPhoneNotice', {
                  phones: info.maskedCollectorPhones.join(', '),
                })}
              </span>
            )}

            <label className="bmwHandover__label">
              {t('NLSOpsBmwHandoverPhoneLabel')}
            </label>
            <TextInput
              aria-label={t('NLSOpsBmwHandoverPhoneLabel')}
              value={phone}
              placeholder={t('NLSOpsBmwHandoverPhonePlaceholder')}
              onChange={setPhone}
            />

            <Button
              variant="primary"
              disabled={busy || !phone.trim()}
              onClick={handleSendOtp}
            >
              {t('NLSOpsBmwHandoverVerifyPhone')}
            </Button>
          </div>
        )}

        {step === 'OTP_ENTRY' && info && (
          <div className="bmwHandover__form">
            <p className="bmwHandover__subtitle">
              {t('NLSOpsBmwHandoverOtpPrompt')}
            </p>

            <label className="bmwHandover__label">
              {t('NLSOpsBmwHandoverOtpLabel')}
            </label>
            <TextInput
              aria-label={t('NLSOpsBmwHandoverOtpLabel')}
              value={otpCode}
              placeholder={t('NLSOpsBmwHandoverOtpPlaceholder')}
              onChange={setOtpCode}
            />

            <label className="bmwHandover__label">
              {t('NLSOpsBmwHandoverOutstandingBags')}
            </label>
            <div className="bmwHandover__bagsList">
              {info.bags.map((bag) => (
                <div key={bag.barcode} className="bmwHandover__bagItem">
                  <div className="bmwHandover__bagInfo">
                    <input
                      type="checkbox"
                      id={`bag_${bag.barcode}`}
                      checked={confirmedBarcodes.includes(bag.barcode)}
                      onChange={() => handleToggleBarcode(bag.barcode)}
                    />
                    <label htmlFor={`bag_${bag.barcode}`}>
                      {bag.barcode} ({bag.color})
                    </label>
                  </div>
                  <span>{bag.weightKg.toFixed(3)} kg</span>
                </div>
              ))}
            </div>

            <Button
              variant="primary"
              disabled={busy || !otpCode.trim() || confirmedBarcodes.length === 0}
              onClick={handleConfirmHandover}
            >
              {t('NLSOpsBmwHandoverConfirmAction')}
            </Button>
          </div>
        )}

        {step === 'CONFIRMED' && result && (
          <div className="bmwHandover__center">
            <span className="bmwHandover__badge bmwHandover__badge--ok">
              {t('NLSOpsBmwHandoverConfirmedTitle')}
            </span>
            <p className="bmwHandover__subtitle">
              {t('NLSOpsBmwHandoverConfirmedDesc')}
            </p>
            <div className="bmwHandover__meta">
              <div className="bmwHandover__metaRow">
                <span className="bmwHandover__metaLabel">{t('NLSOpsBmwHandoverPickupId')}:</span>
                <span className="bmwHandover__metaValue">{result.pickupId}</span>
              </div>
              <div className="bmwHandover__metaRow">
                <span className="bmwHandover__metaLabel">{t('NLSOpsBmwBagCount')}:</span>
                <span className="bmwHandover__metaValue">{result.confirmedCount}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
